import { cache } from 'react';
import { logger } from '@tuic/logger';
import { ApiKitTrip, getMultipleTripsDetails } from '../pcl';
import type { Locale } from '@tuic/i18n';
import { Account } from '../../index';

type BookingCodesToTrips = (args: {
  idToken: string;
  bookingCodes: string[];
  locale: Locale;
}) => Promise<{
  [bookingCode: string]: ApiKitTrip;
}>;

const bookingCodesToTrips: BookingCodesToTrips = async ({
  idToken,
  bookingCodes,
  locale,
}) => {
  let { data, error } = await Account.getMyTrips2({
    auth: idToken,
  });

  if (error) {
    logger.error(
      `Failed to convert bookingCodes to trip. Booking Codes: ${JSON.stringify(bookingCodes)}`,
    );
    logger.error(error);

    return {};
  }

  if (typeof data === 'undefined') {
    return {};
  }

  // map bookingCodes to tripCodes
  const bookingCodeToTripCodeMap = data.reduce<Record<string, string[]>>(
    (acc, booking) => {
      if (!bookingCodes.includes(booking.bookingCode)) {
        return acc;
      }

      return {
        ...acc,
        [booking.bookingCode]: booking.trips.map(trip => trip.tripCode),
      };
    },
    {},
  );

  // strip the mapping down to the tripCodes only in order to fetch the trips from PCL
  const tripCodes = [
    ...new Set(...Object.values(bookingCodeToTripCodeMap).flat()),
  ];

  //  get the trips based on the tripCodes
  const pclTrips = await getMultipleTripsDetails(tripCodes, locale);

  // map the trips onto the bookingCodes
  return bookingCodes.reduce((acc, bookingCode) => {
    // There might be no trip associated with the booking
    if (!bookingCodeToTripCodeMap[bookingCode]) {
      return acc;
    }

    const trip = pclTrips.find(({ code }) => {
      if (typeof code === 'undefined') {
        return false;
      }

      return bookingCodeToTripCodeMap[bookingCode].includes(code);
    });

    if (typeof trip === 'undefined') {
      return acc;
    }

    if (trip) {
      return {
        ...acc,
        [bookingCode]: trip,
      };
    }

    return acc;
  }, {});
};

export const memoizedBookingCodesToTrips: BookingCodesToTrips =
  cache(bookingCodesToTrips);
